import type {
  InFlightAgentActionProposal,
  InFlightTagWritePolicyProposal
} from '@axilon-apps/domains-ontology-ui-state'
import { registerAgentActionView, type AgentActionViewCapability } from './viewRegistry'

function isTagWritePolicy(
  proposal: InFlightAgentActionProposal
): proposal is InFlightTagWritePolicyProposal {
  return proposal.kind === 'tag_write_policy'
}

function targetLabel(proposal: InFlightTagWritePolicyProposal): string {
  const target = proposal.target
  if (target) {
    for (const key of ['display_label', 'tag_path', 'node_id', 'id']) {
      const value = target[key]
      if (typeof value === 'string' && value.trim()) return value
    }
  }
  return proposal.tagId
}

function policyValue(policy: Record<string, unknown>, key: string): string {
  const value = policy[key]
  if (value === undefined || value === null) return 'none'
  return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="break-words">
      <span className="text-muted-foreground">{label}:</span> {value}
    </div>
  )
}

function TagWritePolicyDetail({ proposal }: { proposal: InFlightTagWritePolicyProposal }) {
  const current = policyValue(proposal.currentPolicy, 'disposition')
  const proposed = policyValue(proposal.proposedPolicy, 'disposition')
  const impact =
    proposed === 'ai_auto'
      ? 'Approving marks this tag eligible for autonomous AI writes without a separate value card. This policy approval does not itself perform a value write.'
      : proposed === 'ai_propose'
        ? 'Approving allows agents to create separate value-write approval cards for this tag. Every physical value still requires Apply.'
        : 'Approving allows any authorized viewer of a dashboard containing an explicit control to write this tag. It does not perform a value write.'
  const safety = [
    `min ${policyValue(proposal.proposedPolicy, 'min')}`,
    `max ${policyValue(proposal.proposedPolicy, 'max')}`,
    `max delta ${policyValue(proposal.proposedPolicy, 'max_delta')}`,
    `frequency limit ${policyValue(proposal.proposedPolicy, 'write_frequency_limit_ms')} ms`
  ].join(' · ')

  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-2 text-11 text-foreground">
      <div className="grid gap-1.5">
        <div className="font-medium text-amber-700 dark:text-amber-300">
          Administrator approval required
        </div>
        <DetailRow label="Target" value={targetLabel(proposal)} />
        <DetailRow label="Tag id" value={proposal.tagId} />
        <DetailRow label="Policy change" value={`${current} → ${proposed}`} />
        <DetailRow label="Preserved limits" value={safety} />
        <DetailRow label="Requested use" value={proposal.reason} />
        <p className="rounded-md bg-background/70 p-2 text-10 leading-relaxed text-muted-foreground">
          {impact}
        </p>
      </div>
    </div>
  )
}

function TagWritePolicySettled({ proposal }: { proposal: InFlightTagWritePolicyProposal }) {
  const terminal = proposal.terminalState
  if (!terminal) return null
  if (terminal.kind === 'apply_failed') {
    return <p className="px-1 text-10 text-destructive">Policy update failed — {terminal.error}</p>
  }
  if (terminal.kind === 'rejected') {
    return (
      <p className="px-1 text-10 text-muted-foreground">
        Rejected{proposal.rejectionReason ? ` — ${proposal.rejectionReason}` : ''}. Tag remains
        unchanged.
      </p>
    )
  }
  const disposition = policyValue(proposal.proposedPolicy, 'disposition')
  return (
    <p className="px-1 text-10 text-emerald-600 dark:text-emerald-400">
      {disposition === 'ai_propose'
        ? 'Agent value proposals enabled; physical writes still require separate approval.'
        : disposition === 'ai_auto'
          ? 'Autonomous AI write eligibility enabled; this approval did not write a value.'
          : 'Human dashboard writes enabled.'}{' '}
      Existing bounds and rate limits were preserved.
    </p>
  )
}

export const tagWritePolicyViewCapability: AgentActionViewCapability = {
  kind: 'tag_write_policy',
  requiredApplyPermission: 'tag/write-policy-manage',
  permissionDeniedMessage: 'Administrator approval is required to change tag write policy.',
  badgeLabel() {
    return 'write policy'
  },
  renderDetail(proposal) {
    if (!isTagWritePolicy(proposal)) return null
    return <TagWritePolicyDetail proposal={proposal} />
  },
  renderSettledSummary(proposal) {
    if (!isTagWritePolicy(proposal)) return null
    return <TagWritePolicySettled proposal={proposal} />
  }
}

registerAgentActionView(tagWritePolicyViewCapability)
